import { useCallback } from "react";
import useSWR from "swr";
import { Paginated, PaginationQuery, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { AdminPatrolSupport, UpsertAdminPatrolSupportBody } from "./types/patrolSupports";

export const usePatrolSupportsApi = () => {
  const api = useApi();

  const useAdminPatrolSupports = useCallback(
    (query: PaginationQuery, signal: AbortSignal, systemId?: string) => {
      const mappingUrl = `/admin/settings/patrol/support_mappings`;
      const systemIdUrl = `/admin/settings/patrol/systems/${systemId}/support_mappings`;
      const url = buildUrl(systemId ? systemIdUrl : mappingUrl, query);
      return useSWR<Paginated<AdminPatrolSupport>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminPatrolSupport>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminPatrolSupport = useCallback(
    async (body: UpsertAdminPatrolSupportBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/systems/${body.systemId}/support_mappings`;
      const id = await api.post<{ id: string }, UpsertAdminPatrolSupportBody>(url, body);
      mutateCache([
        `/admin/settings/patrol/support_mappings`,
        `/admin/settings/patrol/systems/${body.systemId}/support_mappings`,
      ]);
      return id;
    },
    [api],
  );

  const updateAdminPatrolSupport = useCallback(
    async (patrolSupportId: string, body: UpsertAdminPatrolSupportBody): Promise<void> => {
      const url = `/admin/settings/patrol/systems/${body.systemId}/support_mappings/${patrolSupportId}`;
      (await api.put<UpsertAdminPatrolSupportBody>(url, body),
        mutateCache([
          "/admin/settings/patrol/support_mappings",
          `/admin/settings/patrol/systems/${body.systemId}/support_mappings`,
        ]));
    },
    [api],
  );

  const deleteAdminPatrolSupport = useCallback(
    async (patrolSupportId: string, systemId?: string): Promise<void> => {
      const url = systemId
        ? `/admin/settings/patrol/systems/${systemId}/support_mappings/${patrolSupportId}`
        : `/admin/settings/patrol/support_mappings/${patrolSupportId}`;
      await api.del(url);
      mutateCache([
        `/admin/settings/patrol/support_mappings`,
        `/admin/settings/patrol/systems/${systemId}/support_mappings`,
      ]);
    },
    [api],
  );

  const updateAdminPatrolSupportVisibility = useCallback(
    async (
      patrolSupportId: string,
      visibility: UpdateVisibilityRequestBody,
      options?: { systemId: string },
    ): Promise<void> => {
      const url = `/admin/settings/patrol/support_mappings/${patrolSupportId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([
        "/admin/settings/patrol/support_mappings",
        `/admin/settings/patrol/systems/${options?.systemId}/support_mappings`,
      ]);
    },
    [api],
  );

  return {
    // getAdminPatrolSupport,
    useAdminPatrolSupports,
    insertAdminPatrolSupport,
    updateAdminPatrolSupport,
    updateAdminPatrolSupportVisibility,
    deleteAdminPatrolSupport,
  };
};
